import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Invoice } from "interface/accounting";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";

interface InvoicesMobileListProps {
	items: Invoice[];
	loading: boolean;
	onOpenPdf: (item: Invoice) => void;
	onLoadMore: () => void;
}

const formatAmount = (value?: number | null) => {
	const asNumber = Number(value || 0);
	return asNumber.toLocaleString("no-NO", {
		maximumFractionDigits: 2,
		style: "currency",
		currency: "NOK",
	});
};

const getStatusClassName = (item: Invoice) => {
	const remainingValue = Number(item.remainingValue || 0);
	const dueDatePassed = item.dueDate
		? new Date(item.dueDate).getTime() < new Date().getTime() && remainingValue !== 0
		: false;

	if (dueDatePassed || remainingValue < 0) {
		return "invoice-list-card--status-red";
	}

	if (remainingValue === 0) {
		return "invoice-list-card--status-green";
	}

	return "invoice-list-card--status-yellow";
};

const InvoicesMobileList: FC<InvoicesMobileListProps> = ({
	items,
	loading,
	onOpenPdf,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	return (
		<div className="invoice-list-mobile" onScroll={handleScroll}>
			{empty && <div className="invoice-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const itemKey = `${item.invoiceNumber}-${item.reportGuid ?? "noreport"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`invoice-list-card ${getStatusClassName(item)}`}
					>
						<div className="invoice-list-card-header">
							<div className="invoice-list-card-note">{item.invoiceNumber || "-"}</div>
							<div className="invoice-list-card-buyer">{item.customerName || "-"}</div>
							<div className="invoice-list-card-date">
								{item.invoiceDate ? (
									<GrideDateFormatter
										date={item.invoiceDate}
										formatType="dd.MM.yyyy"
										noTd={true}
									/>
								) : (
									"-"
								)}
							</div>
						</div>

						<div className="invoice-list-card-body">
							<div>
								<span>{t("InvoiceNumber")}</span>
								<strong>{`${item.invoiceType || ""} ${item.invoiceNumber || "-"}`.trim()}</strong>
							</div>
							<div>
								<span>{t("Buyer")}</span>
								<strong>{item.customerName || "-"}</strong>
							</div>
							<div>
								<span>Forfall</span>
								<strong>
									{item.dueDate ? (
										<GrideDateFormatter
											date={item.dueDate}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									) : (
										"-"
									)}
								</strong>
							</div>
							<div>
								<span>EHF</span>
								<strong>{item.invoiceSentDate ? t("Yes") : t("No")}</strong>
							</div>
							<div>
								<span>Beløp inkl. MVA</span>
								<strong>{formatAmount(item.grossValue)}</strong>
							</div>
							<div>
								<span>Restbeløp</span>
								<strong>{formatAmount(item.remainingValue)}</strong>
							</div>
						</div>

						<div className="invoice-list-card-actions">
							<button
								type="button"
								className="invoice-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img src={`/icon/${ICON_NAMES.Download}.svg`} alt={t("Download")} />
							</button>
						</div>
					</div>
				);
			})}

			{loading && <div className="invoice-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default InvoicesMobileList;